import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { privateRoutes, publicRoutes } from './routerPaths';
import { AppLayout } from '../components/layouts/AppLayout';
import { GlobalRouteNames } from '../models/Common';
import { NotificationsApi } from '../models/PushNotification';
import { Page404 } from '../pages/404';
import { ScrollToTop } from './ScrollToTop';
import { PrivateRoute } from './PrivateRoute';
import { useAppSelector } from '../hooks/redux';
import { AccessDenied } from '../pages/AccessDenied';
import { ErrorPage } from '../pages/Error';
import { hasPermission } from '../utils';
import RecoveryByEmailPage from '../pages/RecoveryByEmailPage/RecoveryByEmailPage';

export const AppRouter: FC<NotificationsApi> = (props) => {
  const { t } = useTranslation();
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { isAuth } = useAppSelector((state) => state.authReducer);
  const { userInfoState } = useAppSelector((state) => state.userInfoReducer);
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const isLogin = location.pathname === GlobalRouteNames.LoginPage;

    const title = isLogin ? t('metaTags.authTitle') : t('metaTags.defaultTitle');
    const description = isLogin ? t('metaTags.authDescription') : t('metaTags.defaultDescription');

    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', description);
  }, [selectedLanguage, location.pathname]);

  useEffect(() => {
    if (
      !isAuth &&
      !location.pathname.includes(GlobalRouteNames.LoginPage) &&
      !location.pathname.includes(GlobalRouteNames.SuccessPublicPage) &&
      !location.pathname.includes(GlobalRouteNames.SuccessAddAccount) &&
      !location.pathname.includes(GlobalRouteNames.TimeOutError) &&
      !location.pathname.includes(GlobalRouteNames.SurveyPage) &&
      !location.pathname.includes(GlobalRouteNames.RecoveryByEmail) &&
      !location.pathname.includes(GlobalRouteNames.RecoveryByEmailWithoutApp) &&
      !privateRoutes.find((route) => !route.exact && location.pathname.includes(String(route.path)))
    ) {
      history.push(GlobalRouteNames.LoginPage);
    }
  }, [isAuth, location.pathname]);

  return (
    <AppLayout {...props}>
      <ScrollToTop />
      <Switch>
        <Route component={RecoveryByEmailPage} path={GlobalRouteNames.RecoveryByEmailWithoutApp} />
        <Route component={RecoveryByEmailPage} path={GlobalRouteNames.RecoveryByEmail} />
        {privateRoutes.map((route) => {
          const { requiredPermissions } = route;
          const hasPermissionResult = hasPermission(userInfoState.Permissions, requiredPermissions);

          return (
            <PrivateRoute
              path={route.path}
              exact={route.exact}
              component={route.component}
              key={route.path}
              hasPermission={hasPermissionResult}
              isAuth={isAuth}
            />
          );
        })}
        {publicRoutes.map((route) => (
          <Route path={route.path} exact={route.exact} component={route.component} key={route.path} />
        ))}
        <Route component={AccessDenied} path={GlobalRouteNames.AccessDenied} />
        <Route component={ErrorPage} path={GlobalRouteNames.UnknownError} />
        <Route component={Page404} path="*" />
        <Redirect to={!isAuth ? GlobalRouteNames.HomePage : GlobalRouteNames.LoginPage} />
      </Switch>
    </AppLayout>
  );
};
